import { SetStateAction, useEffect, useState } from 'react';
import AppConfig from '../AppConfig';
import {
  JWTPayload,
  getToken,
  initAxiosIntances,
  verifyTokenAndInit,
  decodeAuthToken,
} from '../utils/tokenUtils';
import {
  EMessageType,
  IMessagePayload,
  WindowWithWebviewMethods,
} from '../types';
import safeJsonParse from '../utils/safeJsonParse';
import { postMessageToParent } from '../utils/messageUtils';
import { ALLOWED_PARENT_ORIGINS } from '../constants';

export interface IAuthResult {
  isTokenAdded: boolean;
  user: null | JWTPayload;
  isAppReady: boolean;
  setUser: React.Dispatch<SetStateAction<JWTPayload | null>>;
  setIsTokenAdded: React.Dispatch<SetStateAction<boolean>>;
  token: string | null;
  isChatWithPolicyEnabled?: boolean;
  setIsChatWithPolicyEnabled?: React.Dispatch<SetStateAction<boolean>>;
}

const useAuth = (): IAuthResult => {
  const [token, setToken] = useState<string | null>(() => {
    return getToken();
  });
  const [user, setUser] = useState<JWTPayload | null>(() => {
    if (token) {
      const decodedPayload = decodeAuthToken(token);
      return decodedPayload;
    }
    return null;
  });

  const [isTokenAdded, setIsTokenAdded] = useState<boolean>(() => {
    return user ? true : false;
  });
  useEffect(() => {
    const handleEvent = async () => {
      const tokenFromLocalStorage = getToken();
      initAxiosIntances(tokenFromLocalStorage as string);
      const decodedPayload = decodeAuthToken(tokenFromLocalStorage as string);
      setUser(decodedPayload);
      setToken(tokenFromLocalStorage);
      setIsTokenAdded(true);
      (
        window as unknown as WindowWithWebviewMethods
      )?.NativeBridge?.onLoadComplete?.();
    };

    window.addEventListener(
      AppConfig.TOKEN_ADDED_VIA_EXTERNAL_APPLICATION_EVENT as string,
      handleEvent
    );

    window.addEventListener('message', (event) => {
      const { data: message, origin } = event;

      if (
        (import.meta.env.PROD && ALLOWED_PARENT_ORIGINS.includes(origin)) ||
        import.meta.env.DEV
      ) {
        const payload = safeJsonParse(message) as IMessagePayload;

        if (payload == null) {
          return;
        }

        if (payload?.type === EMessageType.TOKEN) {
          verifyTokenAndInit(String(payload?.data));
        }
      }
    });

    if (window.parent) {
      postMessageToParent(EMessageType.READY, [EMessageType.TOKEN]);
    }

    return () => {
      window.removeEventListener(
        AppConfig.TOKEN_ADDED_VIA_EXTERNAL_APPLICATION_EVENT as string,
        handleEvent
      );
    };
  }, [isTokenAdded, token]);

  return {
    isTokenAdded,
    user,
    setUser,
    setIsTokenAdded,
    isAppReady: isTokenAdded,
    token
  };
};

export default useAuth;
